import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { PurchaseRequest } from '@teo/shared';
import { api, ApiRequestError } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { Panel } from '../components/Panel';
import { Modal } from '../components/Overlay';
import { PurchaseTable, ReceivePurchaseModal, purchaseLabel } from '../ops/Purchases';
import { useLoad, useSubmit } from '../lib/useAsync';
import { useToast } from '../lib/useToast';
import { releaseKey, requestKey } from '../lib/idempotency';

const STATES = ['PENDING', 'AUTHORIZED', 'RECEIVED', 'REJECTED', ''] as const;

/** Vista "Autorizaciones": Gerencia o el Autorizador aprueban o rechazan; la recepción la registra la ubicación. */
export function PurchaseAuth() {
  const { t } = useTranslation();
  const { can } = useAuth();
  const { show, showError } = useToast();
  const { busy, run } = useSubmit();
  const [status, setStatus] = useState<string>('PENDING');
  const requests = useLoad(() => api.purchaseRequests({ status: status || undefined, limit: 100 }), [status]);

  const [rejecting, setRejecting] = useState<PurchaseRequest | null>(null);
  const [reason, setReason] = useState('');
  const [creating, setCreating] = useState<PurchaseRequest | null>(null);
  const [product, setProduct] = useState({ ref: '', unit_cost: '', retail_price: '', confirm_similar: false });
  const [receiving, setReceiving] = useState<PurchaseRequest | null>(null);

  const decide = (r: PurchaseRequest, approve: boolean, note: string, extra?: Record<string, unknown>) => {
    const scope = `compra:decision:${r.document_id}`;
    return run(async () => {
      try {
        await api.decidePurchase(requestKey(scope), r.document_id, { approve, note, product: extra });
        releaseKey(scope);
        setRejecting(null); setCreating(null); setReason('');
        setProduct({ ref: '', unit_cost: '', retail_price: '', confirm_similar: false });
        show(t(approve ? 'purchases.authorized' : 'purchases.rejected', { doc: r.doc_no }));
        await requests.reload();
      } catch (err) {
        // Nombre parecido: se ofrece confirmar sin salir del formulario.
        if (err instanceof ApiRequestError && err.code === 'PRODUCT_NAME_SIMILAR') setProduct((p) => ({ ...p, confirm_similar: false }));
        showError(err);
      }
    });
  };

  const authorize = (r: PurchaseRequest) => {
    if (r.request_type === 'NEW_PRODUCT') {
      setCreating(r);
      setProduct({ ref: '', unit_cost: String(r.estimated_cost || ''), retail_price: '', confirm_similar: false });
      return;
    }
    void decide(r, true, '');
  };

  const submitNewProduct = (e: FormEvent) => {
    e.preventDefault();
    if (!creating) return;
    void decide(creating, true, '', {
      ref: product.ref.trim(),
      name: String(creating.proposed?.name ?? ''),
      brand: String(creating.proposed?.brand ?? ''),
      category: String(creating.proposed?.category ?? ''),
      origin: String(creating.proposed?.origin ?? ''),
      stock_granularity: 'NONE',
      confirm_similar: product.confirm_similar,
      prices: { unit_cost: Number(product.unit_cost) || 0, retail_price: Number(product.retail_price) || 0 },
    });
  };

  return (
    <>
      <div className="actions" style={{ marginBottom: 12 }}>
        <div className="field" style={{ minWidth: 220 }}>
          <label htmlFor="authStatus">{t('purchases.filter')}</label>
          <select className="select" id="authStatus" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATES.map((s) => (
              <option key={s || 'all'} value={s}>
                {s ? t(`purchases.status.${s}`) : t('purchases.all')}
              </option>
            ))}
          </select>
        </div>
      </div>

      <Panel title={t('purchases.authTitle')} description={t('purchases.authDesc')} badge={null}>
        <PurchaseTable
          rows={requests.data?.items ?? []}
          loading={requests.loading && !requests.data}
          showLocation
          actions={(r) => (
            <div className="actions">
              {r.status === 'PENDING' ? (
                <>
                  <button type="button" className="btn sm green" data-approve={r.doc_no} disabled={busy} onClick={() => authorize(r)}>
                    {t('purchases.authorize')}
                  </button>
                  <button type="button" className="btn sm red" data-reject={r.doc_no} onClick={() => { setRejecting(r); setReason(''); }}>
                    {t('purchases.reject')}
                  </button>
                </>
              ) : null}
              {r.status === 'AUTHORIZED' && can('purchase.receive') ? (
                <button type="button" className="btn sm" data-receive={r.doc_no} onClick={() => setReceiving(r)}>
                  {t('purchases.receive')}
                </button>
              ) : null}
            </div>
          )}
        />
      </Panel>

      <Modal open={!!rejecting} title={t('purchases.rejectTitle', { doc: rejecting?.doc_no ?? '' })} onClose={() => setRejecting(null)}>
        <form className="formgrid" id="rejectForm" onSubmit={(e) => { e.preventDefault(); if (rejecting) void decide(rejecting, false, reason); }}>
          <div className="field span2">
            <label htmlFor="rejectReason">{t('purchases.reason')}</label>
            <input id="rejectReason" required value={reason} onChange={(e) => setReason(e.target.value)} />
          </div>
          <button className="btn black span2" type="submit" disabled={busy || !reason.trim()}>{t('purchases.reject')}</button>
        </form>
      </Modal>

      <Modal
        open={!!creating}
        title={t('purchases.newProductTitle', { name: creating ? purchaseLabel(creating, t) : '' })}
        help={t('purchases.newProductHelp')}
        onClose={() => setCreating(null)}
      >
        <form className="formgrid" id="newProductForm" onSubmit={submitNewProduct}>
          <div className="field">
            <label htmlFor="npRef">{t('purchases.ref')}</label>
            <input id="npRef" required value={product.ref} onChange={(e) => setProduct({ ...product, ref: e.target.value })} />
          </div>
          <div className="field">
            <label htmlFor="npCost">{t('purchases.unitCost')}</label>
            <input id="npCost" type="number" min={0} value={product.unit_cost} onChange={(e) => setProduct({ ...product, unit_cost: e.target.value })} />
          </div>
          <div className="field">
            <label htmlFor="npPrice">{t('purchases.retailPrice')}</label>
            <input id="npPrice" type="number" min={0} value={product.retail_price} onChange={(e) => setProduct({ ...product, retail_price: e.target.value })} />
          </div>
          <label className="field" htmlFor="npSimilar">
            <span>{t('purchases.confirmSimilar')}</span>
            <input id="npSimilar" type="checkbox" checked={product.confirm_similar} onChange={(e) => setProduct({ ...product, confirm_similar: e.target.checked })} />
          </label>
          <button className="btn black span2" type="submit" id="npSubmit" disabled={busy || !product.ref.trim()}>
            {t('purchases.authorize')}
          </button>
        </form>
      </Modal>

      <ReceivePurchaseModal target={receiving} onClose={() => setReceiving(null)} onDone={() => void requests.reload()} />
    </>
  );
}
